/**
 * rowQuality (#2773 A+B) — THE row classifier behind every completeness /
 * visibility decision in the Deckungsbeitrag app. All five former
 * ``isRevenuelessRow`` call sites (page filter + hiddenable count, verdict,
 * chart pending bars, group rollup, per-kg suppression) consume THIS module,
 * so the chart's mute-set, the table's hide-set, the verdict and the per-kg
 * panel can never disagree about a row again.
 *
 * #2773 B ships the full reason set + the per-reason group-propagation
 * matrix, but stays UI-inert: every call site keys on ``pending`` (the
 * legacy revenueless predicate, unchanged), so consumers are byte-identical
 * wherever no new reason fires. #2773 C decides what the UI does with
 * ``not_applicable`` / ``untrustworthy`` (hide-vs-mark is the product call
 * that subtask carries).
 */
import { db1PerBasisForGroup, isMarginBasis } from './basis';
import type {
  AnalysisUnitRow,
  DeckungsbeitragRow,
  GeometryProvenanceEntry,
  KulturRollupRow,
  ValuationRefusal,
} from './types';

/**
 * The classifier's state vocabulary (#2773):
 * - ``ok`` — nothing disqualifies the row; shown and ranked. May still carry
 *   marker-only reasons (``leistung_modelled``) and, on rows that don't look
 *   complete, informational cost/source reasons.
 * - ``pending`` — costs booked, no revenue yet: incomplete, not wrong. Hidden
 *   by default (status quo).
 * - ``not_applicable`` — definitionally without a Deckungsbeitrag (non-
 *   marketable land). Producer: the #2773 D worker stamp.
 * - ``untrustworthy`` — looks complete (``leistung > 0``) but a trust signal
 *   fired. NEVER to be silently hidden (#2773 C renders that promise).
 *
 * State precedence (deterministic): ``not_applicable`` > ``pending`` >
 * ``untrustworthy`` > ``ok``. Grünland without revenue is definitionally
 * without a DB (not "pending"); a revenueless row is not yet a WRONG number,
 * so pending wins over trust reasons — which still ride in ``reasons[]`` for
 * the drawer.
 */
export type RowQualityState = 'ok' | 'pending' | 'not_applicable' | 'untrustworthy';

/**
 * Reason tokens travel WITH the state so the UI can say WHY (#2773 C).
 *
 * | reason | fires on | state class |
 * |---|---|---|
 * | ``no_revenue_booked`` | the legacy revenueless predicate | pending |
 * | ``not_marketable`` | worker-stamped key (#2773 D) | not_applicable |
 * | ``leistung_modelled`` | ``leistung_estimated`` (#2695 E) | marker-only — NEVER flips state (R4) |
 * | ``required_cost_bucket_missing`` | a required bucket 0 € AND not modelled | untrustworthy |
 * | ``denominator_untrusted`` | ``area_ha`` null; unknown geometry tier | untrustworthy |
 * | ``per_basis_implausible`` | worker-stamped band breach — DB1 je kg / je Pflanze (#2773 E) | untrustworthy |
 * | ``thin_source`` | category fallback / too few source lines | untrustworthy |
 * | ``cost_coverage_low`` | worker-stamped coverage flag (#2769 b) | untrustworthy |
 * | ``leistung_unpriced`` | ``leistung_incomplete`` (HOF-102 D2) | untrustworthy |
 *
 * ``cost_coverage_low`` is an EXACT-token contract with the worker's
 * conditional fact key (5.5 review sole-ownership ruling: this subtask owns
 * the FE side; #2769 b emits the key and ships no FE edits).
 */
export type RowQualityReason =
  | 'no_revenue_booked'
  | 'not_marketable'
  | 'leistung_modelled'
  | 'required_cost_bucket_missing'
  | 'denominator_untrusted'
  | 'per_basis_implausible'
  | 'thin_source'
  | 'cost_coverage_low'
  | 'leistung_unpriced';

/** Canonical emission order — ``reasons[]`` is always sorted by this, so copy reads stably. */
export const REASON_ORDER: readonly RowQualityReason[] = [
  'no_revenue_booked',
  'not_marketable',
  'leistung_modelled',
  'required_cost_bucket_missing',
  'denominator_untrusted',
  'per_basis_implausible',
  'thin_source',
  'cost_coverage_low',
  'leistung_unpriced',
];

export interface RowQuality {
  state: RowQualityState;
  /**
   * Every reason that fired, in ``REASON_ORDER``. NOT empty-iff-ok: marker
   * reasons (``leistung_modelled``) and — on rows that don't look complete —
   * trust reasons ride along on ``ok``/``pending`` states so the drawer can
   * always say why (#2773 C).
   */
  reasons: RowQualityReason[];
  /**
   * HOF-140 — WHICH refusal put ``leistung_unpriced`` on this grain, when every
   * unpriced Posten under it agrees. The per-Posten vocabulary is a closed set
   * precisely so the FE can explain each member (`leistung_valuation.py`), and the
   * chip the farmer reads sits one grain above the Posten: without this it had one
   * sentence and hardcoded the ``no_price`` one, so a Posten refused ``incomplete``
   * was told the Preisspiegel had no price for a month that held 2.336 of them.
   *
   * **Absent, never defaulted, and that is the whole design.** It is omitted on a
   * MIXED grain (Posten refused for different reasons), on a fact whose
   * ``leistung_posten`` block this app has none of or rejected, and on any row that
   * did not fire ``leistung_unpriced`` at all. Which reason wins on a mixed row is a
   * product-copy decision with three defensible answers (most severe / most frequent
   * / a compound sentence) and it is Antonio's, not the classifier's — so an
   * abstention here is what keeps ``chipsFor`` on today's wording rather than letting
   * a default pick the answer silently. Conditional-key emission, the same discipline
   * the worker's fact payload follows: absent means *not claimed*.
   */
  unpricedReason?: ValuationRefusal;
}

/**
 * Config-not-code (v1, #2771 migration candidates): the rule VALUES live in
 * named exported constants — never hand-mirrored YAML — so the future served-
 * config move (#2771, v31.11) swaps one constant source, not scattered
 * literals.
 */
/**
 * The cost buckets a complete-looking row must cover somehow. Saatgut and
 * Löhne are the discriminating pair per the issue's evidence table (WF's
 * maschinen/sonstige are structurally zero until #2769's apportionment, and
 * Dünger/PSM legitimately round to 0 on small cycles) — and the pair keeps
 * discriminating after #2769 lands either side of this subtask.
 */
export const REQUIRED_COST_BUCKETS = ['saatgut', 'loehne'] as const;
export type RequiredCostBucket = (typeof REQUIRED_COST_BUCKETS)[number];

/**
 * An operations-path row summed from fewer raw lines than this is thin
 * evidence. Conservative v1: only 0/1-line rows fire — the
 * ``category_fallback`` arm of ``thin_source`` already covers the
 * structurally thin class.
 */
export const THIN_SOURCE_MIN_LINE_COUNT = 2;

/**
 * The #2303 geometry tiers whose derivation chain rests entirely on RECORDED
 * crop_cycle attributes (deterministic arithmetic — the honesty line in the
 * drawer states the assumption, the value is not distrusted). Any tier
 * OUTSIDE this set (a future estimated/assumed density) fires
 * ``denominator_untrusted`` without needing an FE release: unknown ⇒
 * untrusted, mirroring the STAND_PROVENANCES fail-honest posture.
 */
export const TRUSTED_GEOMETRY_SOURCES: ReadonlySet<string> = new Set([
  'recorded',
  'derived_from_row_spacing',
  // HOF-312 — the Reihenabstand recorded on the SCHLAG. Deterministic arithmetic over a
  // recorded attribute, exactly like the crop_cycle tier above; the honesty line names the
  // Schlag so a farmer can tell the farm convention from a per-planting measurement.
  'derived_from_field_row_spacing',
  'derived_from_plants_per_m',
  'derived_from_plants_per_m2',
  // The Schlag's registered tunnels (Reihen × Länge). Deterministic arithmetic over recorded
  // tunnel dimensions, so the value is not distrusted; the honesty line names the tunnels.
  'derived_from_tunnels',
]);

/** The revenueless predicate's minimal signal set (also the rollup's sums shape). */
type RevenueSignals = Pick<DeckungsbeitragRow, 'leistung' | 'db1'>;

/** The signals ``classifyRow`` consumes — the Pick documents exactly what the verdict rests on. */
export type RowQualitySignals = Pick<
  DeckungsbeitragRow,
  | 'leistung'
  | 'db1'
  | 'area_ha'
  | 'row_length_m'
  | 'plant_count'
  | 'geometry_provenance'
  | 'direct_costs'
  | 'saatgut_estimate'
  | 'establishment_amortised'
  | 'leistung_estimated'
  | 'source'
  | 'source_line_count'
  | 'not_marketable'
  | 'per_basis_implausible'
  | 'cost_coverage_low'
  | 'leistung_incomplete'
  // HOF-140 — the per-Posten refusal reasons, read ONLY to explain a
  // ``leistung_unpriced`` that ``leistung_incomplete`` already declared. It never
  // decides WHETHER the reason fires: the fact's own flag stays authoritative, so a
  // block this app rejected still carries the warning (``types.ts``).
  | 'leistung_posten'
>;

/**
 * The "Unvollständig" completeness criterion behind the yellow verdict and
 * the muted (pending) chart bars: costs are booked but no revenue is in yet
 * (``leistung == 0`` while real costs pushed db1 below the leistung). An
 * all-zero row (leistung 0, db1 0) is NOT revenueless — ``0 > 0`` is false —
 * so a genuinely empty cycle stays visible.
 *
 * #2773 — no longer consumed directly by the UI: ``classifyRow`` /
 * ``classifyGroupSums`` are the source of truth, and this predicate is their
 * internal ``no_revenue_booked`` producer. It stays exported (via
 * ``deckungsbeitragRollup`` too) for the tests that pin its truth table.
 */
export function isRevenuelessRow(row: RevenueSignals): boolean {
  return row.leistung === 0 && row.leistung - row.db1 > 0;
}

/** The reasons whose class is untrustworthy (see the ``RowQualityReason`` matrix). */
const UNTRUSTWORTHY_REASONS: ReadonlySet<RowQualityReason> = new Set<RowQualityReason>([
  'required_cost_bucket_missing',
  'denominator_untrusted',
  'per_basis_implausible',
  'thin_source',
  'cost_coverage_low',
  // HOF-102 D2 — untrustworthy rather than marker-only, and the direction is why: an
  // unpriced Posten contributes 0 €, so a row that looks complete UNDERSTATES its own
  // Leistung by however many kilos went unpriced. That is a wrong number, not an absent
  // one. ``stateOf`` only claims the state for ``leistung > 0``, so WF's 2016-2020
  // cycles — incomplete on nearly every Posten, and therefore Leistung 0 — stay
  // ``pending`` and are not re-labelled by this.
  'leistung_unpriced',
]);

/**
 * Is a required bucket covered SOMEHOW? Three states per bucket — booked /
 * modelled / missing — and only *missing* fires (#2773 B): a modelled Saatgut
 * leg (``saatgut_estimate``, incl. the #2593 stand verdicts whose 0,00 € is
 * the rule's answer) and an amortised Pflanzgut leg (#2608
 * ``establishment_amortised``) are answers, not gaps. Löhne has no modelled
 * form today — booked or missing.
 *
 * #4172 wave 2 widened the Löhne leg to the whole WAGE, not the ``loehne`` bucket alone.
 * The wave split one bucket into several, so on a wiberry cycle the entire wage can sit in
 * ``pflueck`` (a summer pick books picking money and no Pflege at all) — and reading only
 * ``loehne`` there would fire „Kosten fehlen" on a fact that MEASURED its labour down to
 * the person-day. That is the failure mode this signal exists to prevent, inverted: it
 * would accuse the best-evidenced rows on the farm. The backend's
 * ``deckungsbeitrag_row_quality._LABOUR_BUCKETS`` carries the same pair, and the two must
 * agree or the drawer and the discovery tools disagree about the same row.
 *
 * 2026-10-07 — ``pflanzung`` joins: the drawer prints Pflanzarbeit inside the Löhne line, so
 * a freshly planted cycle whose whole wage is planting must not read „Kosten fehlen" beside a
 * Löhne line that shows the money.
 */
export const LABOUR_COVERAGE_BUCKETS = ['loehne', 'pflueck', 'pflanzung'] as const;

function bucketCovered(row: RowQualitySignals, bucket: RequiredCostBucket): boolean {
  switch (bucket) {
    case 'saatgut':
      return row.direct_costs.saatgut !== 0 || row.saatgut_estimate !== null || row.establishment_amortised !== 0;
    case 'loehne':
      return LABOUR_COVERAGE_BUCKETS.some((name) => row.direct_costs[name] !== 0);
  }
}

/**
 * #2773 B — the geometry leg of ``denominator_untrusted``, scoped per the
 * manifest correction: ``geometry_provenance`` describes how the Pflanze /
 * Reihenmeter denominators were derived — it is NOT an ``area_ha`` trust
 * signal, and a present-but-wrong area is deliberately left to the #2773 E
 * plausibility band. Only an OFFERED basis (quantity present) whose tier is
 * outside ``TRUSTED_GEOMETRY_SOURCES`` fires.
 */
function geometryTierUntrusted(row: RowQualitySignals): boolean {
  const provenance = row.geometry_provenance;
  if (!provenance) return false;
  const offered: Array<[number | null, GeometryProvenanceEntry | undefined]> = [
    [row.row_length_m, provenance.row_length_m],
    [row.plant_count, provenance.plant_count],
  ];
  return offered.some(
    ([quantity, entry]) => quantity !== null && entry !== undefined && !TRUSTED_GEOMETRY_SOURCES.has(entry.source),
  );
}

/**
 * HOF-140 — the ONE refusal behind a row's unpriced Posten, or ``undefined``.
 *
 * Three ways to abstain, and each is a population this row cannot enumerate rather than
 * a tie it declines to break:
 *
 * 1. **No readable block.** ``leistung_posten`` is null both on the empty-price-book
 *    branch (no block was ever written) and when the mapper rejected a block carrying a
 *    token this app has no copy for. Neither reached row grain, so neither may be explained.
 * 2. **The block declares more refusals than it carries.** ``refused_count`` is the
 *    worker's own declaration and ``items`` is the evidence; the mapper cross-checks
 *    neither (it validates each item, not the count). A block claiming two refusals while
 *    carrying one would let that one speak for a refusal nobody can see. Found by codex.
 * 3. **A refusal carrying no reason.** Typed ``ValuationRefusal | null``, and the mapper
 *    only leaves it null on a VALUED Posten — so this is unreachable through it, but
 *    leaning on that silently is the point. It rides along as ``null`` and poisons
 *    unanimity rather than being filtered out.
 *
 * 2 and 3 are the same defect wearing different clothes, and it is the defect this whole
 * change exists to remove, one grain down: a claim asserted over a population wider than
 * the evidence for it.
 */
function unanimousRefusalOf(row: RowQualitySignals): ValuationRefusal | undefined {
  const block = row.leistung_posten;
  if (!block) return undefined;
  const refusals = block.items.filter((item) => !item.valued).map((item) => item.reason);
  if (refusals.length !== block.refused_count) return undefined;
  const first = refusals[0];
  if (first === undefined || first === null) return undefined;
  return refusals.every((refusal) => refusal === first) ? first : undefined;
}

/**
 * The precedence fold (see ``RowQualityState``). ``untrustworthy`` is claimed
 * ONLY for rows that look complete (``leistung > 0``): a leistung-0 row's
 * number is absent-or-partial, not wrong, so its trust reasons ride
 * marker-style on ``ok``/``pending`` instead of claiming a state.
 */
function stateOf(reasons: RowQualityReason[], leistung: number): RowQualityState {
  if (reasons.includes('not_marketable')) return 'not_applicable';
  if (reasons.includes('no_revenue_booked')) return 'pending';
  if (leistung > 0 && reasons.some((reason) => UNTRUSTWORTHY_REASONS.has(reason))) return 'untrustworthy';
  return 'ok';
}

/**
 * Classify ONE row (one deckungsbeitrag fact). Reasons are emitted in
 * ``REASON_ORDER``; the state is the precedence fold over them.
 */
export function classifyRow(row: RowQualitySignals): RowQuality {
  const reasons: RowQualityReason[] = [];
  if (isRevenuelessRow(row)) reasons.push('no_revenue_booked');
  if (row.not_marketable === true) reasons.push('not_marketable');
  if (row.leistung_estimated) reasons.push('leistung_modelled');
  if (REQUIRED_COST_BUCKETS.some((bucket) => !bucketCovered(row, bucket))) reasons.push('required_cost_bucket_missing');
  if (row.area_ha === null || geometryTierUntrusted(row)) reasons.push('denominator_untrusted');
  if (row.per_basis_implausible != null) reasons.push('per_basis_implausible');
  if (row.source === 'category_fallback' || row.source_line_count < THIN_SOURCE_MIN_LINE_COUNT) {
    reasons.push('thin_source');
  }
  if (row.cost_coverage_low === true) reasons.push('cost_coverage_low');
  if (row.leistung_incomplete) reasons.push('leistung_unpriced');
  const quality: RowQuality = { state: stateOf(reasons, row.leistung), reasons };
  // Gated on the REASON, not on the block: a block whose items disagree with the fact's
  // own flag is an inconsistent fact, and explaining a chip the row does not carry is
  // worse than the silence.
  if (reasons.includes('leistung_unpriced')) {
    const only = unanimousRefusalOf(row);
    if (only !== undefined) quality.unpricedReason = only;
  }
  return quality;
}

/**
 * The Kultur-rollup call site's SUMS-grain application, isolated behind its
 * own name (#2773 A): a group is pending iff the row predicate holds over the
 * GROUP SUMS — so one booked member un-pends the whole Kultur (status quo,
 * documented at ``rollupByKultur``). Deliberately UNCHANGED by #2773 B: it
 * feeds the rollup's ``pending`` flag (the chart's Kultur-mode mute bit), and
 * the per-reason matrix lives in ``classifyGroup``.
 */
export function classifyGroupSums(sums: RevenueSignals): RowQuality {
  if (isRevenuelessRow(sums)) return { state: 'pending', reasons: ['no_revenue_booked'] };
  return { state: 'ok', reasons: [] };
}

/**
 * HOF-195 — the three reasons whose verdict is RE-DERIVED at the analysis-unit grain
 * rather than folded up from the blocks. Named as a set so ``classifyAnalysisUnit``'s
 * propagation loop and its re-derivation arm read from ONE list and cannot drift into
 * deciding a reason twice or not at all.
 */
const UNIT_REDERIVED_REASONS: ReadonlySet<RowQualityReason> = new Set<RowQualityReason>([
  'per_basis_implausible',
  'thin_source',
]);

/** What ``classifyAnalysisUnit`` reads — the Sum, and the blocks it was summed from. */
export type AnalysisUnitQualitySignals = Pick<
  AnalysisUnitRow,
  'leistung' | 'db1' | 'db1_per_ha' | 'members'
>;

/**
 * HOF-195 — re-judge ``per_basis_implausible`` on the UNIT's own denominator.
 *
 * The verdict is a statement about a denominator, and the unit has a different one: WF's
 * blocks are judged on 0,44-0,71 ha each, the unit on 2,2632 ha. Propagating the block
 * verdict upward would badge the united row *unplausibel* on the strength of exactly the
 * grain Max says is untrustworthy — the complaint this issue exists to dissolve.
 *
 * The band is read off a member's own stamped block (#2773 E stamps
 * ``{basis, value, band, band_key}`` so the verdict stays interpretable later), which is
 * present precisely when there is a breach to re-judge. No new config channel, and no
 * fleet-wide constant to keep in sync across two surfaces.
 *
 * Returns ``null`` for UNDECIDABLE, and the caller then keeps the ``some(member)`` fold.
 * Three ways to be undecidable, and each is the same shape: the unit cannot compute the
 * number the band judges. No usable band; a stamped basis this app cannot name; no unit
 * denominator (one member without an area already nulls the Sum). A softening we cannot
 * prove is a softening we must not make.
 *
 * A FOURTH way, and the one that is not obvious: the stamped blocks disagreeing with each
 * other. The spec assumed one band per Kultur+Sorte group and licensed taking the first —
 * but a band is a *rule revision's* opinion, and facts are not all re-materialised at
 * once, so two members can legitimately carry different bounds for the same crop. Taking
 * "the first" would then make the unit's badge depend on MEMBER ORDER, which changes with
 * the active Bezugsgröße. Unanimity or degrade — the same discipline ``unanimousRefusalOf``
 * applies to refusals one grain down, and for the same reason: a claim asserted over a
 * population wider than the evidence for it. Found by codex.
 *
 * **Unanimity is asked of the STAMPED members only, and deliberately so.** Codex's fix
 * round asked for it across *every* member — the worry being that an unflagged block may
 * have been materialised under an older revision's band. That requirement is
 * unsatisfiable by construction: the worker emits this block ONLY on a breach
 * (`deckungsbeitrag.py:2296`), so an unflagged member never carries a band and never can.
 * Demanding one would restrict the re-judgement to units where EVERY block breached —
 * exactly the case where a weighted mean cannot clear the badge anyway — making the
 * clearing arm unreachable and dissolving criterion 6, which is the point of the level.
 * That is the same "branch that cannot fire" defect codex rightly found in the coverage
 * recompute, reintroduced under another name. The concern also does not reach the
 * arithmetic: the unit's value is Σ over the members' `db1`/`area_ha` — numbers on the
 * facts — not over their verdicts, so which band a member was once judged against does
 * not change what it contributes.
 *
 * **Every stamped BASIS is re-judged, not just the first (2026-10-06).** The worker's band
 * left the hectare and now judges DB1 je kg and je Pflanze, so a block can breach on two
 * bases at once: the block names the first and carries the other in ``also``. The unit is
 * judged basis by basis — breached on any => flagged; undecidable on any (and breached on
 * none) => the fold stands; cleared only when it is back in band on ALL of them. Reading
 * only the leading basis would let a unit shed the badge on its kilogrammes while its
 * plants are still credited with a harvest no plant carries. Unanimity is asked per basis,
 * so two blocks that breached on DIFFERENT bases no longer make each other undecidable.
 */
function unitBandVerdict(unit: AnalysisUnitQualitySignals): boolean | null {
  const stamped = unit.members.flatMap((m) => {
    const block = m.per_basis_implausible;
    if (block == null) return [];
    return [block, ...(block.also ?? [])].map((breach) => ({ ...breach, band_key: block.band_key }));
  });
  if (stamped.length === 0) return false; // nothing breached => nothing to re-judge
  let undecidable = false;
  for (const basis of new Set(stamped.map((b) => b.basis))) {
    const onBasis = stamped.filter((b) => b.basis === basis);
    const block = onBasis[0];
    const sameBand = (other: typeof block): boolean =>
      other.band_key === block.band_key &&
      (other.band?.min ?? null) === (block.band?.min ?? null) &&
      (other.band?.max ?? null) === (block.band?.max ?? null);
    const band = block.band;
    const value =
      onBasis.every(sameBand) && band && (band.min !== null || band.max !== null) && isMarginBasis(basis)
        ? db1PerBasisForGroup(unit, basis)
        : null;
    if (value === null || !band) {
      undecidable = true;
      continue;
    }
    if (band.min !== null && value < band.min) return true;
    if (band.max !== null && value > band.max) return true;
  }
  return undecidable ? null : false;
}

/**
 * HOF-195 — why ``cost_coverage_low`` is NOT recomputed at the unit grain, though the
 * spec's matrix asked for it. Its premise — *"coverage counts add"* — is true of the
 * arithmetic and false of the facts.
 *
 * The worker emits the ``cost_coverage`` evidence block **only inside the flagged
 * branch** (`worker/src/services/materialisers/deckungsbeitrag.py:2196-2198`), and the
 * mapper closes the loop from the other side: a row carrying a usable block is flagged by
 * that block alone (`deckungsbeitragMappers.ts`, the #2695 E hoist). So an unflagged
 * member carries neither the flag nor the counts, and **there is no Σ to take** — the
 * denominator of the united ratio is unknowable, not zero.
 *
 * What survives is exactly ``some(member)``: with every member flagged a Σ-ratio is a
 * weighted mean and can never exceed the largest member ratio, so the unit stays flagged;
 * with none flagged nothing fires; and mixed is undecidable. An earlier draft shipped the
 * bounded-threshold recompute for all three cases — correct arithmetic over a fact shape
 * the producer cannot emit, i.e. a branch that could not fire and a test that built an
 * impossible row to reach it. Deleted rather than left dark: this repo has been bitten by
 * checks that cannot fail, and a branch that cannot run is the same defect standing up.
 * Found by codex.
 *
 * If the producer ever emits coverage on unflagged rows too, the recompute becomes
 * reachable and worth restoring — bounded by the members' own verdicts, never by a
 * hardcoded ratio, because ``DECKUNGSBEITRAG_COST_COVERAGE_WARN_RATIO`` is env-tunable
 * worker-side and is never served to this app.
 */

/**
 * HOF-195 — recompute ``thin_source`` at the unit grain. Evidence counts add, so two
 * one-line blocks really are two lines of evidence together and the unit clears the
 * threshold its members individually failed.
 *
 * The ``category_fallback`` arm keeps the ``some(member)`` fold and is deliberately NOT
 * recomputed: it is not a count, it is a statement that a block's money never came from
 * individual bookings at all, and adding line counts around it would let a well-evidenced
 * sibling vouch for evidence that does not exist.
 */
function unitThinSource(unit: AnalysisUnitQualitySignals): boolean {
  if (unit.members.some((m) => m.source === 'category_fallback')) return true;
  return unit.members.reduce((acc, m) => acc + m.source_line_count, 0) < THIN_SOURCE_MIN_LINE_COUNT;
}

/**
 * HOF-195 — classify one Hauptschlag analysis unit.
 *
 * This is #2773 B's *"decide per reason, say why"* discipline applied to a grain it had
 * not met, and the split is principled rather than case-by-case: **a reason whose inputs
 * ADD is recomputed at the coarser grain; a reason that marks a number WRONG stays
 * sticky, because summing a wrong number keeps it wrong.**
 *
 * | reason | at unit grain | why |
 * |---|---|---|
 * | ``no_revenue_booked`` | predicate over the UNIT sums | one booked block makes the Sum partially real (the ``classifyGroup`` rule, unchanged) |
 * | ``not_marketable`` | ``every(member)`` | degrades SAFE — a mixed unit shows rather than hides real money |
 * | ``per_basis_implausible`` | **re-judged** on the unit's own denominator | the verdict is a statement about a denominator, and the unit has a different one |
 * | ``thin_source`` | **recomputed** over Sum ``source_line_count`` | evidence counts add |
 * | ``cost_coverage_low`` | ``some(member)`` | the counts WOULD add, but the producer emits them on flagged rows only — see the note above ``unitThinSource`` |
 * | ``denominator_untrusted`` | ``some(member)`` | a block with no area already makes the Sum area incomplete |
 * | ``required_cost_bucket_missing`` | ``some(member)`` | not in the extension table: a booked sibling must not vouch for a block whose bucket is empty |
 * | ``leistung_unpriced``, ``leistung_modelled`` | ``some(member)`` | these mark a WRONG number; summing it keeps it wrong |
 *
 * Both re-derived reasons degrade to the ``some(member)`` fold where the evidence cannot
 * decide, so this can only ever be MORE cautious than the status quo, never less. Grain-honest copy (#2809) is the copy module's half of the same promise:
 * a ``some()``-propagated reason on a unit says „mindestens ein Block …".
 */
export function classifyAnalysisUnit(unit: AnalysisUnitQualitySignals): RowQuality {
  const memberQualities = unit.members.map((member) => classifyRow(member));
  const memberReasons = memberQualities.map((quality) => quality.reasons);
  const someMember = (reason: RowQualityReason): boolean =>
    memberReasons.some((reasons) => reasons.includes(reason));
  const fired = new Set<RowQualityReason>();
  if (isRevenuelessRow(unit)) fired.add('no_revenue_booked');
  if (memberReasons.length > 0 && memberReasons.every((reasons) => reasons.includes('not_marketable'))) {
    fired.add('not_marketable');
  }
  for (const reasons of memberReasons) {
    for (const reason of reasons) {
      if (UNIT_REDERIVED_REASONS.has(reason)) continue; // decided below, on the unit's own inputs
      if (reason === 'leistung_modelled' || UNTRUSTWORTHY_REASONS.has(reason)) fired.add(reason);
    }
  }
  // ``??`` is the degrade: an undecidable re-derivation falls back to the member fold.
  if (unitBandVerdict(unit) ?? someMember('per_basis_implausible')) fired.add('per_basis_implausible');
  if (unitThinSource(unit)) fired.add('thin_source');
  const ordered = REASON_ORDER.filter((reason) => fired.has(reason));
  const quality: RowQuality = { state: stateOf(ordered, unit.leistung), reasons: ordered };
  // HOF-140, one grain down: the unit explains itself only when every SHORT block tells
  // the same story — a mixed block cannot add up to a clean unit.
  if (fired.has('leistung_unpriced')) {
    const short = memberQualities.filter((member) => member.reasons.includes('leistung_unpriced'));
    const first = short[0]?.unpricedReason;
    if (first !== undefined && short.every((member) => member.unpricedReason === first)) {
      quality.unpricedReason = first;
    }
  }
  return quality;
}

/**
 * Classify one already-rolled-up Kultur+Sorte group, applying the per-reason
 * propagation matrix (#2773 B — the #2809 discipline: decide per reason, say
 * why):
 *
 * | reason | row → group | rationale |
 * |---|---|---|
 * | ``no_revenue_booked`` | predicate over GROUP SUMS | one booked member makes the Σ partially real (status quo, ``rollupByKultur``) |
 * | ``not_marketable`` | ``every(member)`` | constant within a group by construction (the key includes the crop); ``every`` degrades SAFE — a mixed group shows rather than hides real money |
 * | untrustworthy reasons | ``some(member)`` | one hollow member already understates the group Σ — the ``leistung_estimated`` argument |
 * | ``leistung_modelled`` | ``some(member)`` | the existing #2695 E fold, unchanged |
 *
 * Grain-honest copy discipline (#2809): any group/betrieb-grain rendering of
 * a ``some()``-propagated reason must say "mindestens ein Schlag …" — the
 * flag does not claim every member (that copy lands in #2773 C).
 */
export function classifyGroup(group: Pick<KulturRollupRow, 'leistung' | 'db1' | 'members'>): RowQuality {
  const memberQualities = group.members.map((member) => classifyRow(member));
  const memberReasons = memberQualities.map((quality) => quality.reasons);
  const fired = new Set<RowQualityReason>();
  if (isRevenuelessRow(group)) fired.add('no_revenue_booked');
  if (memberReasons.length > 0 && memberReasons.every((reasons) => reasons.includes('not_marketable'))) {
    fired.add('not_marketable');
  }
  for (const reasons of memberReasons) {
    for (const reason of reasons) {
      if (reason === 'leistung_modelled' || UNTRUSTWORTHY_REASONS.has(reason)) fired.add(reason);
    }
  }
  const ordered = REASON_ORDER.filter((reason) => fired.has(reason));
  const quality: RowQuality = { state: stateOf(ordered, group.leistung), reasons: ordered };
  // HOF-140 — the group explains itself only when every SHORT member tells the same
  // story. Folded over the members' own verdicts rather than over their raw Posten, so a
  // member that is itself mixed (``unpricedReason`` absent) poisons the group by
  // construction: a mixed Schlag cannot add up to a clean Kultur.
  if (fired.has('leistung_unpriced')) {
    const short = memberQualities.filter((member) => member.reasons.includes('leistung_unpriced'));
    const first = short[0]?.unpricedReason;
    if (first !== undefined && short.every((member) => member.unpricedReason === first)) {
      quality.unpricedReason = first;
    }
  }
  return quality;
}
